import { RangeModifier, Button } from '@wheely/ui-kit';
import { Locale } from 'date-fns';
import addWeeks from 'date-fns/addWeeks';
import formatDuration from 'date-fns/formatDuration';
import intervalToDuration from 'date-fns/intervalToDuration';
import isThisWeek from 'date-fns/isThisWeek';
import subWeeks from 'date-fns/subWeeks';
import { useCallback, useMemo, useContext } from 'react';
import { useIntl, IntlShape, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { IntlContext } from '@/i18n';
import type { THourlyWorkloads, IWorkloadSummary } from '@/modules/driver/model/driver-workload';
import { getCity } from '@/modules/partner/store/selectors';
import { DescriptionList } from '@/shared/view/DescriptionList';
import { Icon } from '@src-common/view/components/Icon';

import s from './TabWorkingHours.module.css';
import { WeekPicker } from './WeekPicker';
import { WeekSelector } from './WeekSelector';
import { WorkingHoursCalendar } from './WorkingHoursCalendar';

interface IProps {
  workloads: THourlyWorkloads | null;
  week: RangeModifier;
  summary: IWorkloadSummary | null;
  onWeekChange: (week: RangeModifier) => void;
}

type TTitles = 'totalTime' | 'weekAmount';

type TTitleLine = [TTitles, string];

const getTotalTime = (minutes: number, locale: Locale) => {
  const duration = intervalToDuration({ start: 0, end: minutes * 60 * 1000 });

  return formatDuration(duration, {
    format: ['days', 'hours', 'minutes'],
    locale,
  });
};

const getButtonTitle = (from: Date, to: Date, intl: IntlShape) =>
  `${intl.formatDate(from, {
    month: '2-digit',
    day: 'numeric',
  })} — ${intl.formatDate(to, {
    month: '2-digit',
    day: 'numeric',
  })}`;

const WorkingHours = ({ workloads, week, summary, onWeekChange }: IProps) => {
  const intl = useIntl();
  const currencyCode = useSelector(getCity)?.currencyCode;
  const { dateFnsLocale } = useContext(IntlContext);

  const paginateWeek = useCallback(
    (mode: 'next' | 'prev') => () => {
      if (week.from && week.to) {
        const changeWeek = mode === 'next' ? addWeeks : subWeeks;

        onWeekChange({
          from: changeWeek(week.from, 1),
          to: changeWeek(week.to, 1),
        });
      }
    },
    [onWeekChange, week],
  );

  const paginateToPrevWeek = paginateWeek('prev');
  const paginateToNextWeek = paginateWeek('next');

  const isNextButtonDisabled = week.from ? isThisWeek(week.from) : false;

  const weekSelectorTitle = useMemo(() => {
    if (week.from && week.to) {
      return getButtonTitle(week.from, week.to, intl);
    }

    return '';
  }, [week, intl]);

  const titles = useMemo(
    (): Array<TTitleLine> => [
      [
        'totalTime',
        intl.formatMessage({
          defaultMessage: 'Total hours online',
          id: 'kOQ0td',
          description: 'Working hours summary',
        }),
      ],
      [
        'weekAmount',
        intl.formatMessage({
          defaultMessage: 'Week amount',
          id: '93cxnK',
          description: 'Working hours summary',
        }),
      ],
    ],
    [intl],
  );

  const values = useMemo(
    () => ({
      totalTime: getTotalTime(summary?.totalMinutes || 0, dateFnsLocale),
      weekAmount: intl.formatNumber(summary?.amount || 0, {
        style: 'currency',
        currency: currencyCode,
      }),
    }),
    [intl, summary, dateFnsLocale, currencyCode],
  );

  return (
    <>
      <div className={s.controls}>
        <Button icon={<Icon icon="arrow-back" />} onClick={paginateToPrevWeek} minimal={true}>
          <span className={s.paginationButtonLabel}>
            <FormattedMessage
              defaultMessage="Previous week"
              id="10Kel6"
              description="Working hours pagination"
            />
          </span>
        </Button>
        <WeekSelector buttonTitle={weekSelectorTitle}>
          <WeekPicker week={week} onWeekChange={onWeekChange} />
        </WeekSelector>
        <Button
          rightIcon={<Icon icon="arrow-next" />}
          onClick={paginateToNextWeek}
          minimal={true}
          disabled={isNextButtonDisabled}
        >
          <span className={s.paginationButtonLabel}>
            <FormattedMessage
              defaultMessage="Next week"
              id="nlzx0B"
              description="Working hours pagination"
            />
          </span>
        </Button>
      </div>
      <WorkingHoursCalendar workloads={workloads} />
      <DescriptionList
        title={intl.formatMessage({
          defaultMessage: 'Statistics',
          id: 'ilje8/',
          description: 'Driver screen, working hours tab: statistics section heading',
        })}
        values={values}
        titles={titles}
      />
    </>
  );
};

export type { IProps };
export { WorkingHours };
